import { ROOM_SAMPLE_RATE, resample } from '@eduvy/core';

/**
 * Microphone capture as 16 kHz PCM16 in 20 ms frames. Echo cancellation,
 * noise suppression and auto gain are the browser's; together with the
 * server's barge-in threshold they keep the AI from hearing itself.
 */
const WORKLET = `
class PcmCapture extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(2048); this.n = 0; this.muted = false;
    this.port.onmessage = (e) => { this.muted = !!e.data.muted; }; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.buf[this.n++] = this.muted ? 0 : ch[i];
        if (this.n === this.buf.length) { this.port.postMessage(this.buf.slice(0)); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor('pcm-capture', PcmCapture);
`;

export class MicCapture {
  private constructor(
    private readonly ctx: AudioContext,
    private readonly stream: MediaStream,
    private readonly node: AudioWorkletNode,
    private readonly analyser: AnalyserNode,
  ) {}

  static async start(onPcm: (frame: Int16Array) => void): Promise<MicCapture> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
    let ctx: AudioContext;
    let source: MediaStreamAudioSourceNode;
    try {
      // Let the browser resample the mic to 16 kHz where it can.
      ctx = new AudioContext({ sampleRate: ROOM_SAMPLE_RATE });
      source = ctx.createMediaStreamSource(stream);
    } catch {
      ctx = new AudioContext();
      source = ctx.createMediaStreamSource(stream);
    }
    const url = URL.createObjectURL(new Blob([WORKLET], { type: 'application/javascript' }));
    await ctx.audioWorklet.addModule(url);
    URL.revokeObjectURL(url);
    const node = new AudioWorkletNode(ctx, 'pcm-capture');
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    source.connect(node);
    // The worklet needs a path to the destination to be pulled; send it silence.
    const sink = ctx.createGain();
    sink.gain.value = 0;
    node.connect(sink).connect(ctx.destination);

    const rate = ctx.sampleRate;
    let pending = new Int16Array(0);
    node.port.onmessage = (e: MessageEvent<Float32Array>) => {
      const f = e.data;
      const pcm = new Int16Array(f.length);
      for (let i = 0; i < f.length; i++) {
        const s = Math.max(-1, Math.min(1, f[i]!));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      const at16k = rate === ROOM_SAMPLE_RATE ? pcm : resample(pcm, rate, ROOM_SAMPLE_RATE);
      const merged = new Int16Array(pending.length + at16k.length);
      merged.set(pending);
      merged.set(at16k, pending.length);
      let off = 0;
      for (; off + 320 <= merged.length; off += 320) onPcm(merged.slice(off, off + 320));
      pending = merged.slice(off);
    };
    if (ctx.state === 'suspended') await ctx.resume();
    return new MicCapture(ctx, stream, node, analyser);
  }

  /** 0..1, for the level meter. */
  level(): number {
    const data = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (const v of data) sum += ((v - 128) / 128) ** 2;
    return Math.min(1, Math.sqrt(sum / data.length) * 4);
  }

  setMuted(muted: boolean): void {
    this.node.port.postMessage({ muted });
    for (const t of this.stream.getAudioTracks()) t.enabled = !muted;
  }

  stop(): void {
    for (const t of this.stream.getTracks()) t.stop();
    void this.ctx.close();
  }
}

/**
 * Plays the AI's clips in order through <audio> elements (which browsers'
 * echo cancellers reliably reference), and reports each one as played.
 */
export class ClipPlayer {
  private queue: Array<{ id: number; url: string }> = [];
  private current: { id: number; el: HTMLAudioElement; url: string } | null = null;

  constructor(
    private readonly onPlayed: (id: number) => void,
    private readonly onActivity: (playing: boolean) => void,
  ) {}

  /** Call from a click handler: browsers only allow audio after a user gesture. */
  unlock(): void {
    const el = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=');
    void el.play().catch(() => undefined);
  }

  get playing(): boolean {
    return this.current !== null;
  }

  enqueue(id: number, wav: ArrayBuffer): void {
    const url = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
    this.queue.push({ id, url });
    if (!this.current) this.next();
  }

  clear(): void {
    if (this.current) {
      this.current.el.pause();
      URL.revokeObjectURL(this.current.url);
      this.current = null;
    }
    for (const q of this.queue) URL.revokeObjectURL(q.url);
    this.queue = [];
    this.onActivity(false);
  }

  private next(): void {
    const item = this.queue.shift();
    if (!item) {
      this.current = null;
      this.onActivity(false);
      return;
    }
    const el = new Audio(item.url);
    this.current = { ...item, el };
    this.onActivity(true);
    const done = () => {
      if (this.current?.id !== item.id) return;
      URL.revokeObjectURL(item.url);
      this.onPlayed(item.id);
      this.next();
    };
    el.onended = done;
    el.onerror = done;
    el.play().catch(done);
  }
}
